import React, { useState, useEffect } from 'react';
import { Users, Building2, Settings, Plus, Edit, Trash2, Shield, Crown, UserCheck, AlertTriangle, CheckCircle } from 'lucide-react';
import { supabase } from '../config/supabase';

interface Empresa {
  id: string;
  nome: string;
  cnpj: string;
  razao_social: string;
  status: string;
  numero_funcionarios: number;
  created_at: string;
}

interface Usuario {
  id: string;
  email: string;
  nome_completo: string;
  tipo_usuario: string;
  ativo: boolean;
  created_at: string;
}

interface UsuarioEmpresa {
  id: string;
  usuario_id: string;
  empresa_id: string;
  tipo_acesso: string;
  ativo: boolean;
  usuario: Usuario;
  empresa: Empresa;
}

interface ParametroPerfil {
  id: string;
  tipo_usuario: string;
  max_empresas: number;
  max_eleitores: number;
  max_funcionarios: number;
  descricao: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState('empresas');
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [usuarioEmpresas, setUsuarioEmpresas] = useState<UsuarioEmpresa[]>([]);
  const [parametrosPerfil, setParametrosPerfil] = useState<ParametroPerfil[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'empresa' | 'usuario' | 'relacionamento' | 'parametro'>('empresa');
  const [editingItem, setEditingItem] = useState<any>(null);

  // Estados para formulários
  const [empresaForm, setEmpresaForm] = useState({
    nome: '',
    cnpj: '',
    razao_social: '',
    endereco: '',
    telefone: '',
    email: '',
    responsavel_legal: '',
    cpf_responsavel: '',
    grau_risco: '2',
    numero_funcionarios: 0,
    data_termino_mandato: '',
    observacoes: ''
  });

  const [usuarioForm, setUsuarioForm] = useState({
    email: '',
    nome_completo: '',
    tipo_usuario: 'BASICO',
    ativo: true
  });

  const [relacionamentoForm, setRelacionamentoForm] = useState({
    usuario_id: '',
    empresa_id: '',
    tipo_acesso: 'GESTOR',
    ativo: true
  });

  const [parametroForm, setParametroForm] = useState({
    tipo_usuario: '',
    max_empresas: 1,
    max_eleitores: 50,
    max_funcionarios: 50,
    descricao: '',
    ativo: true
  });

  const [eleitoresIlimitados, setEleitoresIlimitados] = useState(false);
  const [funcionariosIlimitados, setFuncionariosIlimitados] = useState(false);

  // Carregar dados
  const carregarDados = async () => {
    try {
      setLoading(true);
      
      // Carregar empresas
      const { data: empresasData } = await supabase
        .from('cp_empresas')
        .select('*')
        .order('nome');

      // Carregar usuários
      const { data: usuariosData } = await supabase
        .from('cp_usuarios')
        .select('*')
        .order('nome_completo');

      // Carregar relacionamentos
      const { data: relacionamentosData } = await supabase
        .from('cp_usuario_empresas')
        .select(`
          *,
          usuario:cp_usuarios(*),
          empresa:cp_empresas(*)
        `)
        .order('created_at', { ascending: false });

      // Carregar parâmetros de perfil
      const { data: parametrosData } = await supabase
        .from('cp_parametros_perfil')
        .select('*')
        .order('tipo_usuario');

      setEmpresas(empresasData || []);
      setUsuarios(usuariosData || []);
      setUsuarioEmpresas(relacionamentosData || []);
      setParametrosPerfil(parametrosData || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  // Salvar empresa
  const salvarEmpresa = async () => {
    try {
      // Preparar dados para inserção/atualização
      const dadosEmpresa = {
        ...empresaForm,
        // Converter string vazia para null para campos de data
        data_termino_mandato: empresaForm.data_termino_mandato || null,
        // Converter string vazia para null para outros campos opcionais
        cnpj: empresaForm.cnpj || null,
        razao_social: empresaForm.razao_social || null,
        endereco: empresaForm.endereco || null,
        telefone: empresaForm.telefone || null,
        email: empresaForm.email || null,
        responsavel_legal: empresaForm.responsavel_legal || null,
        cpf_responsavel: empresaForm.cpf_responsavel || null,
        observacoes: empresaForm.observacoes || null
      };

      if (editingItem) {
        const { error } = await supabase
          .from('cp_empresas')
          .update(dadosEmpresa)
          .eq('id', editingItem.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('cp_empresas')
          .insert([dadosEmpresa]);
        
        if (error) throw error;
      }
      
      setShowModal(false);
      setEditingItem(null);
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar empresa:', error);
      alert('Erro ao salvar empresa');
    }
  };

  // Salvar usuário
  const salvarUsuario = async () => {
    try {
      if (editingItem) {
        const { error } = await supabase
          .from('cp_usuarios')
          .update(usuarioForm)
          .eq('id', editingItem.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('cp_usuarios')
          .insert([usuarioForm]);
        
        if (error) throw error;
      }
      
      setShowModal(false);
      setEditingItem(null);
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      alert('Erro ao salvar usuário');
    }
  };

  // Salvar relacionamento
  const salvarRelacionamento = async () => {
    try {
      if (editingItem) {
        const { error } = await supabase
          .from('cp_usuario_empresas')
          .update(relacionamentoForm)
          .eq('id', editingItem.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('cp_usuario_empresas')
          .insert([relacionamentoForm]);
        
        if (error) throw error;
      }
      
      setShowModal(false);
      setEditingItem(null);
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar relacionamento:', error);
      alert('Erro ao salvar relacionamento');
    }
  };

  // Salvar parâmetro de perfil
  const salvarParametro = async () => {
    try {
      const dadosParametro = {
        ...parametroForm,
        max_eleitores: eleitoresIlimitados ? -1 : parametroForm.max_eleitores,
        max_funcionarios: funcionariosIlimitados ? -1 : parametroForm.max_funcionarios
      };

      if (editingItem) {
        const { error } = await supabase
          .from('cp_parametros_perfil')
          .update(dadosParametro)
          .eq('id', editingItem.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('cp_parametros_perfil')
          .insert([dadosParametro]);
        
        if (error) throw error;
      }
      
      setShowModal(false);
      setEditingItem(null);
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar parâmetro:', error);
      alert('Erro ao salvar parâmetro');
    }
  };

  // Deletar item
  const deletarItem = async (tabela: string, id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este item?')) return;
    
    try {
      const { error } = await supabase
        .from(tabela)
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      carregarDados();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      alert('Erro ao deletar item');
    }
  };

  // Abrir modal
  const abrirModal = (tipo: 'empresa' | 'usuario' | 'relacionamento' | 'parametro', item?: any) => {
    setModalType(tipo);
    setEditingItem(item || null);
    
    if (item) {
      if (tipo === 'empresa') setEmpresaForm(item);
      if (tipo === 'usuario') setUsuarioForm(item);
      if (tipo === 'relacionamento') setRelacionamentoForm(item);
      if (tipo === 'parametro') {
        setParametroForm(item);
        setEleitoresIlimitados(item.max_eleitores === -1);
        setFuncionariosIlimitados(item.max_funcionarios === -1);
      }
    } else {
      // Resetar formulários
      setEmpresaForm({
        nome: '', cnpj: '', razao_social: '', endereco: '', telefone: '', email: '',
        responsavel_legal: '', cpf_responsavel: '', grau_risco: '2', numero_funcionarios: 0,
        data_termino_mandato: '', observacoes: ''
      });
      setUsuarioForm({ email: '', nome_completo: '', tipo_usuario: 'BASICO', ativo: true });
      setRelacionamentoForm({ usuario_id: '', empresa_id: '', tipo_acesso: 'GESTOR', ativo: true });
      setParametroForm({ tipo_usuario: '', max_empresas: 1, max_eleitores: 50, max_funcionarios: 50, descricao: '', ativo: true });
      setEleitoresIlimitados(false);
      setFuncionariosIlimitados(false);
    }
    
    setShowModal(true);
  };

  const TabButton = ({ id, icon: Icon, label, active, onClick }: any) => (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
        active 
          ? 'bg-blue-600 text-white shadow-lg' 
          : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );

  const getTipoUsuarioIcon = (tipo: string) => {
    switch (tipo) {
      case 'SUPER_ADMIN': return <Crown className="w-4 h-4 text-purple-600" />;
      case 'ENTERPRISE': return <Shield className="w-4 h-4 text-blue-600" />;
      case 'PROFISSIONAL': return <UserCheck className="w-4 h-4 text-green-600" />;
      default: return <Users className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      'ATIVA': 'bg-green-100 text-green-800',
      'INATIVA': 'bg-gray-100 text-gray-800',
      'SUSPENSA': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando painel de administração...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Crown className="w-8 h-8 text-purple-600" />
                Painel de Administração
              </h1>
              <p className="text-gray-600 mt-1">Gerenciar usuários, empresas e relacionamentos</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-600">Total de Empresas</p>
                <p className="text-2xl font-bold text-blue-600">{empresas.length}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Total de Usuários</p>
                <p className="text-2xl font-bold text-green-600">{usuarios.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 py-4">
            <TabButton
              id="empresas"
              icon={Building2}
              label="Empresas"
              active={activeTab === 'empresas'}
              onClick={setActiveTab}
            />
            <TabButton
              id="usuarios"
              icon={Users}
              label="Usuários"
              active={activeTab === 'usuarios'}
              onClick={setActiveTab}
            />
            <TabButton
              id="relacionamentos"
              icon={Settings}
              label="Relacionamentos"
              active={activeTab === 'relacionamentos'}
              onClick={setActiveTab}
            />
            <TabButton
              id="parametros"
              icon={Shield}
              label="Parâmetros de Perfil"
              active={activeTab === 'parametros'}
              onClick={setActiveTab}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Empresas Tab */}
        {activeTab === 'empresas' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Empresas Cadastradas</h2>
              <button
                onClick={() => abrirModal('empresa')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Nova Empresa
              </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CNPJ</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Funcionários</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {empresas.map((empresa) => (
                      <tr key={empresa.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{empresa.nome}</div>
                            <div className="text-sm text-gray-500">{empresa.razao_social}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{empresa.cnpj}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{empresa.numero_funcionarios}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(empresa.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => abrirModal('empresa', empresa)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deletarItem('cp_empresas', empresa.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Usuários Tab */}
        {activeTab === 'usuarios' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Usuários do Sistema</h2>
              <button
                onClick={() => abrirModal('usuario')}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Novo Usuário
              </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {usuarios.map((usuario) => (
                      <tr key={usuario.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{usuario.nome_completo}</div>
                            <div className="text-sm text-gray-500">{usuario.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getTipoUsuarioIcon(usuario.tipo_usuario)}
                            <span className="text-sm text-gray-900">{usuario.tipo_usuario}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {usuario.ativo ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 inline mr-1" />
                              Ativo
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertTriangle className="w-3 h-3 inline mr-1" />
                              Inativo
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => abrirModal('usuario', usuario)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deletarItem('cp_usuarios', usuario.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Relacionamentos Tab */}
        {activeTab === 'relacionamentos' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Relacionamentos Usuário-Empresa</h2>
              <button
                onClick={() => abrirModal('relacionamento')}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Novo Relacionamento
              </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo de Acesso</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {usuarioEmpresas.map((rel) => (
                      <tr key={rel.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{rel.usuario.nome_completo}</div>
                            <div className="text-sm text-gray-500">{rel.usuario.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{rel.empresa.nome}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {rel.tipo_acesso}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {rel.ativo ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Ativo
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Inativo
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => abrirModal('relacionamento', rel)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deletarItem('cp_usuario_empresas', rel.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Parâmetros Tab */}
        {activeTab === 'parametros' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Parâmetros de Perfil</h2>
              <button
                onClick={() => abrirModal('parametro')}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Novo Parâmetro
              </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo de Usuário</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max Empresas</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max Eleitores</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max Funcionários</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {parametrosPerfil.map((parametro) => (
                      <tr key={parametro.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getTipoUsuarioIcon(parametro.tipo_usuario)}
                            <span className="text-sm font-medium text-gray-900">{parametro.tipo_usuario}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {parametro.max_empresas === -1 ? 'Ilimitado' : parametro.max_empresas}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {parametro.max_eleitores === -1 ? 'Ilimitado' : parametro.max_eleitores}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {parametro.max_funcionarios === -1 ? 'Ilimitado' : parametro.max_funcionarios}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {parametro.descricao}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {parametro.ativo ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 inline mr-1" />
                              Ativo
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertTriangle className="w-3 h-3 inline mr-1" />
                              Inativo
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => abrirModal('parametro', parametro)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deletarItem('cp_parametros_perfil', parametro.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">
              {editingItem ? 'Editar' : 'Novo'} {modalType === 'empresa' ? 'Empresa' : modalType === 'usuario' ? 'Usuário' : modalType === 'relacionamento' ? 'Relacionamento' : 'Parâmetro de Perfil'}
            </h3>
            
            {modalType === 'empresa' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                    <input
                      type="text"
                      value={empresaForm.nome}
                      onChange={(e) => setEmpresaForm({...empresaForm, nome: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ</label>
                    <input
                      type="text"
                      value={empresaForm.cnpj}
                      onChange={(e) => setEmpresaForm({...empresaForm, cnpj: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Razão Social</label>
                  <input
                    type="text"
                    value={empresaForm.razao_social}
                    onChange={(e) => setEmpresaForm({...empresaForm, razao_social: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={empresaForm.email}
                      onChange={(e) => setEmpresaForm({...empresaForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                    <input
                      type="text"
                      value={empresaForm.telefone}
                      onChange={(e) => setEmpresaForm({...empresaForm, telefone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grau de Risco</label>
                    <select
                      value={empresaForm.grau_risco}
                      onChange={(e) => setEmpresaForm({...empresaForm, grau_risco: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="1">1 - Baixo</option>
                      <option value="2">2 - Médio</option>
                      <option value="3">3 - Alto</option>
                      <option value="4">4 - Muito Alto</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número de Funcionários</label>
                    <input
                      type="number"
                      value={empresaForm.numero_funcionarios}
                      onChange={(e) => setEmpresaForm({...empresaForm, numero_funcionarios: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {modalType === 'usuario' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo</label>
                  <input
                    type="text"
                    value={usuarioForm.nome_completo}
                    onChange={(e) => setUsuarioForm({...usuarioForm, nome_completo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={usuarioForm.email}
                    onChange={(e) => setUsuarioForm({...usuarioForm, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Usuário</label>
                  <select
                    value={usuarioForm.tipo_usuario}
                    onChange={(e) => setUsuarioForm({...usuarioForm, tipo_usuario: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="BASICO">Básico</option>
                    <option value="PROFISSIONAL">Profissional</option>
                    <option value="ENTERPRISE">Enterprise</option>
                    <option value="SUPER_ADMIN">Super Admin</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={usuarioForm.ativo}
                    onChange={(e) => setUsuarioForm({...usuarioForm, ativo: e.target.checked})}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Usuário Ativo</label>
                </div>
              </div>
            )}

            {modalType === 'relacionamento' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usuário</label>
                  <select
                    value={relacionamentoForm.usuario_id}
                    onChange={(e) => setRelacionamentoForm({...relacionamentoForm, usuario_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione um usuário</option>
                    {usuarios.map(usuario => (
                      <option key={usuario.id} value={usuario.id}>{usuario.nome_completo}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                  <select
                    value={relacionamentoForm.empresa_id}
                    onChange={(e) => setRelacionamentoForm({...relacionamentoForm, empresa_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione uma empresa</option>
                    {empresas.map(empresa => (
                      <option key={empresa.id} value={empresa.id}>{empresa.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Acesso</label>
                  <select
                    value={relacionamentoForm.tipo_acesso}
                    onChange={(e) => setRelacionamentoForm({...relacionamentoForm, tipo_acesso: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="VISUALIZADOR">Visualizador</option>
                    <option value="GESTOR">Gestor</option>
                    <option value="ADMINISTRADOR">Administrador</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={relacionamentoForm.ativo}
                    onChange={(e) => setRelacionamentoForm({...relacionamentoForm, ativo: e.target.checked})}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Relacionamento Ativo</label>
                </div>
              </div>
            )}

            {modalType === 'parametro' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Usuário</label>
                  <select
                    value={parametroForm.tipo_usuario}
                    onChange={(e) => setParametroForm({...parametroForm, tipo_usuario: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={editingItem} // Não permitir alterar tipo de usuário ao editar
                  >
                    <option value="">Selecione um tipo</option>
                    <option value="BASICO">Básico</option>
                    <option value="PROFISSIONAL">Profissional</option>
                    <option value="ENTERPRISE">Enterprise</option>
                    <option value="SUPER_ADMIN">Super Admin</option>
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Máximo de Empresas</label>
                    <input
                      type="number"
                      value={parametroForm.max_empresas}
                      onChange={(e) => setParametroForm({...parametroForm, max_empresas: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="1"
                      placeholder="Use -1 para ilimitado"
                    />
                    <p className="text-xs text-gray-500 mt-1">Use -1 para ilimitado</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Máximo de Eleitores</label>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={eleitoresIlimitados}
                          onChange={(e) => {
                            setEleitoresIlimitados(e.target.checked);
                            if (e.target.checked) {
                              setParametroForm({...parametroForm, max_eleitores: 0});
                            }
                          }}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700">Ilimitado</label>
                      </div>
                      {!eleitoresIlimitados && (
                        <input
                          type="number"
                          value={parametroForm.max_eleitores}
                          onChange={(e) => setParametroForm({...parametroForm, max_eleitores: parseInt(e.target.value) || 0})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="1"
                          placeholder="Digite o número máximo"
                        />
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Máximo de Funcionários</label>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={funcionariosIlimitados}
                          onChange={(e) => {
                            setFuncionariosIlimitados(e.target.checked);
                            if (e.target.checked) {
                              setParametroForm({...parametroForm, max_funcionarios: 0});
                            }
                          }}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700">Ilimitado</label>
                      </div>
                      {!funcionariosIlimitados && (
                        <input
                          type="number"
                          value={parametroForm.max_funcionarios}
                          onChange={(e) => setParametroForm({...parametroForm, max_funcionarios: parseInt(e.target.value) || 0})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="1"
                          placeholder="Digite o número máximo"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <textarea
                    value={parametroForm.descricao}
                    onChange={(e) => setParametroForm({...parametroForm, descricao: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Descrição do perfil e suas limitações"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={parametroForm.ativo}
                    onChange={(e) => setParametroForm({...parametroForm, ativo: e.target.checked})}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">Parâmetro Ativo</label>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (modalType === 'empresa') salvarEmpresa();
                  if (modalType === 'usuario') salvarUsuario();
                  if (modalType === 'relacionamento') salvarRelacionamento();
                  if (modalType === 'parametro') salvarParametro();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingItem ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
